/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Theme, Typography, useTheme
  ,
} from '@mui/material';
import {
  blue, common, grey, lightBlue,
} from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectPayPalAccount, UpdatePayPalSetAsDefault } from '../../../store/slices/dashboardSlice';
import { useSolutionOrg } from '../../../config/useSolutionOrg';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import ConnectWithPaypalCTA from '../../../common/ConnectWithPaypalCTA';
import { TestIds } from '../../../assets/testIds';
import { ReactComponent as PaypalLogo } from '../../../assets/images/paypal_logo.svg';
import { PaymentMethodStatus, PayoutDialogContentType } from '../../../common/enums';
import { TransparentPayoutCTA } from './Common';
import { NavigationTarget, useNavigationUtils } from '../../../common/utils/useNavigationUtils';

const paypalButtonStyle = (theme: Theme) => ({
  height: '100%',
  padding: `${theme.spacing(1)} ${theme.spacing(2.25)}`,
  background: lightBlue[700],
  borderRadius: theme.spacing(8),
  '&:hover': {
    backgroundColor: blue[900],
  },
  '&:disabled': {
    backgroundColor: lightBlue[700],
    opacity: '0.4',
    color: common.white,
  },
  [theme.breakpoints.up('sm')]: {
    width: '100%',
    marginLeft: theme.spacing(0),
  },
} as const);

interface PaypalVerificationCardProps {
  setDialogContentType?: (event: PayoutDialogContentType) => void;
  setDialogSubTitle?: (event: string | JSX.Element) => void;
  setPaymentMethodId?: (event: string) => void;
}

export default function PaypalVerificationCard({
  setDialogContentType,
  setDialogSubTitle,
  setPaymentMethodId,
}: PaypalVerificationCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const payPalAccount = useAppSelector(selectPayPalAccount);
  const theme = useTheme();
  const { paypalConnectionReturnUrl } = useSolutionOrg();
  const [checkAcknowledge, setCheckAcknowledge] = useState(false);
  const [checkDefault, setCheckDefault] = useState(false);
  const { trackPayoutSetting } = useCustomEventTracking();
  const { openExternalLink } = useNavigationUtils();

  useEffect(() => {
    if (payPalAccount && payPalAccount?.status !== PaymentMethodStatus.UNLINKED) {
      setDialogSubTitle?.(`${t('wallet.payoutSettingsDialog.subTitle.managePayPalAccount')}`);
    } else {
      setDialogSubTitle?.(`${t('wallet.payoutSettingsDialog.subTitle.connectNewPayPal')}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveAccount = () => {
    setDialogContentType?.(PayoutDialogContentType.REMOVE_ACCOUNT);
    const newSubTitle = (
      <>
        {t('wallet.payoutSettingsDialog.subTitle.delete')}
        <Typography
          variant='body4'
          component='span'
          sx={{
            [theme.breakpoints.down('sm')]: {
              fontSize: theme.spacing(2),
            },
          }}
        >
          {`PayPal (${payPalAccount?.meta?.paypalAccountId})?`}
        </Typography>
      </>
    );
    setPaymentMethodId?.(payPalAccount?.id || '');
    setDialogSubTitle?.(newSubTitle);
  };

  const handleDefaultChecked = () => {
    setCheckDefault(!checkDefault);
  };

  const openPaypal = () => {
    const returnUrl = paypalConnectionReturnUrl;
    const appClientId = process.env.REACT_APP_PAYPAL_APP_ID;
    const paypalBaseUrl = process.env.REACT_APP_PAYPAL_CONNECTION_BASE_URL;
    const scopes = 'email';
    trackPayoutSetting('Click', 'ConnectWithPayPal');
    dispatch(UpdatePayPalSetAsDefault(checkDefault));
    openExternalLink(
      `${paypalBaseUrl}/?flowEntry=static&client_id=${appClientId}&response_type=code&scope=${scopes}&redirect_uri=${returnUrl}&fullPage=false`,
      NavigationTarget.Webview,
    );
  };

  const PaymentMethodCard = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        flex: '1 0 0',
        padding: theme.spacing(2),
        alignSelf: 'stretch',
        borderRadius: theme.spacing(1),
        border: `${theme.spacing(0.125)} solid #E9EAEB`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          padding: `${theme.spacing(1.125)} ${theme.spacing(2.625)} ${theme.spacing(1)} ${theme.spacing(2.4)}`,
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: theme.spacing(0.75),
          border: `${theme.spacing(0.125)} solid #E9EAEB`,
          height: theme.spacing(6.25),
          width: theme.spacing(8.75),
        }}
      >
        {<PaypalLogo height='auto' width='auto' />}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: theme.spacing(1),
          alignSelf: 'stretch',
          flex: '1 0 0',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            alignItems: {
              xs: 'flex-start',
              sm: 'center',
            },
            gap: theme.spacing(2),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 0 0',
              alignSelf: 'center',
            }}
          >
            <Typography variant='h6'>
              {t('wallet.payoutSettingsDialog.payPal')}
            </Typography>
            < Typography
              variant='body8'
              sx={{
                wordBreak: 'break-all',
                overflowWrap: 'break-word',
                marginRight: theme.spacing(4),
              }}
            >
              {payPalAccount?.meta?.paypalAccountId}
            </Typography>
          </Box>
          <ConnectWithPaypalCTA
            data-testid={TestIds.paypalVerification.connectWithPaypal.connectBtn}
            text={`${t('button.updatePayPal')}`}
            type={'submit'}
            sx={[paypalButtonStyle, {
              alignSelf: {
                xs: 'stretch',
                sm: 'center',
              },
              width: {
                xs: '100%',
                sm: 'auto',
              },
            }]}
            disable={!checkAcknowledge}
            onClick={openPaypal}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(2),
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Checkbox
            data-testid={TestIds.paypalVerification.connectWithPaypal.acknowledgeCheckbox}
            checked={checkAcknowledge}
            onChange={() => setCheckAcknowledge(!checkAcknowledge)}
            sx={{
              p: theme.spacing(0.5),
              '&:hover': {
                backgroundColor: theme.palette.brand.surface,
              },
              '& .MuiSvgIcon-root': {
                color: grey[300],
              },
              '&.Mui-checked .MuiSvgIcon-root': {
                color: theme.palette.brand.primary,
              },
            }}
          />
          <Typography
            variant='body8'
            color={`${grey[800]}99`}
          >
            I acknowledge that the PayPal account I am connecting is associated with our company.
          </Typography>
        </Box>
        {payPalAccount && (payPalAccount?.status !== PaymentMethodStatus.UNLINKED &&
          payPalAccount?.status !== PaymentMethodStatus.UNVERIFIED) ? <>
            <PaymentMethodCard />
            <TransparentPayoutCTA
              text={t('button.removeAccount')}
              onClick={handleRemoveAccount}
            />
          </> :
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'stretch',
              }}
            >
              <Checkbox
                data-testid={TestIds.managePayoutSettings.activeCheckbox}
                checked={checkDefault}
                onChange={handleDefaultChecked}
                sx={{
                  p: theme.spacing(0.5),
                  '&:hover': {
                    backgroundColor: theme.palette.brand.surface,
                  },
                  '& .MuiSvgIcon-root': {
                    color: grey[300],
                  },
                  '&.Mui-checked .MuiSvgIcon-root': {
                    color: theme.palette.brand.primary,
                  },
                }}
              />
              <Typography variant='body8' color={`${grey[800]}99`}>
                {t('wallet.payoutSettingsDialog.setActive')}
              </Typography>
            </Box>
            <Box sx={{
              display: 'flex',
            }}>
              <ConnectWithPaypalCTA
                data-testid={TestIds.paypalVerification.connectWithPaypal.connectBtn}
                text={`${t('button.connectWithPaypal')}`}
                type={'submit'}
                sx={[paypalButtonStyle]}
                disable={!checkAcknowledge}
                onClick={openPaypal}
              />
            </Box>
          </>
        }
      </Box>
    </>
  );
}
